// src/pages/SchutzgeraetBlock.jsx
// UMZ-/Schutzgeraet eines Leistungsschalterfelds (Anna-Lena 18.09.2026, Migration 58).
// Stammdaten am MS-Feld (station_ms_felder.schutzgeraet, jsonb) - dieselbe Maske im
// Stammdaten-Formular (Buero) und im Protokoll (Kapitel 02, Monteur liest am Geraet ab).
// Das Schutzpruefprotokoll selbst laedt das Buero als Datei hoch (SchutzpruefDateien).

import { useState, useEffect, useCallback } from 'react';
import { C, Field, TextInput, Row } from './formui';
import { supabase, withTimeout } from '../lib/supabase';
import { t } from '../lib/sprache';
import { LS_FELDER } from '../lib/anlagenRegeln';

export const hatSchutzgeraet = (f) => LS_FELDER.includes(f?.typ_code)
  || !!(f?.schutzgeraet && Object.values(f.schutzgeraet).some(v => v != null && v !== ''));

const EINSTELLWERTE = [
  ['i_gt', 'I> [A]'], ['t_gt', 't> [s]'],
  ['i_gtgt', 'I>> [A]'], ['t_gtgt', 't>> [s]'],
  ['ie_gt', 'IE> [A]'], ['te_gt', 'tE> [s]'],
];

export default function SchutzgeraetBlock({ wert, onChange }) {
  const w = wert || {};
  const setzen = (k, v) => onChange({ ...w, [k]: v === '' ? null : v });
  return (
    <div style={{ marginTop: 8, padding: 10, background: C.white, borderRadius: 7, border: '1px solid ' + C.gray200 }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: C.navy, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
        {t('UMZ- / Schutzgerät')}
      </div>
      <Row columns={2}>
        <Field label={t('Hersteller')}><TextInput value={w.hersteller} onChange={v => setzen('hersteller', v)} placeholder={t('z. B. Woodward, Siemens, ABB')}/></Field>
        <Field label={t('Typ')}><TextInput value={w.typ} onChange={v => setzen('typ', v)} placeholder={t('z. B. WIC1, 7SJ, REJ')}/></Field>
      </Row>
      <Row columns={2}>
        <Field label={t('Seriennummer')}><TextInput value={w.seriennummer} onChange={v => setzen('seriennummer', v)}/></Field>
        <Field label={t('In Schaltfeld [A]')}><TextInput value={w.in_schaltfeld} onChange={v => setzen('in_schaltfeld', v)} placeholder={t('z. B. 630')}/></Field>
      </Row>
      {/* Wandler und Relais (Anna-Lena 22.09.2026) - bleibt im selben jsonb-Feld,
          dafuer ist keine Datenbank-Aenderung noetig. */}
      <Row columns={2}>
        <Field label={t('Wandlerübersetzung')}><TextInput value={w.wandler} onChange={v => setzen('wandler', v)} placeholder={t('z. B. 100/1 A')}/></Field>
        <Field label={t('Wandlerleistung [VA]')}><TextInput value={w.wandler_leistung_va} onChange={v => setzen('wandler_leistung_va', v)} placeholder={t('z. B. 5')}/></Field>
      </Row>
      <Row columns={2}>
        <Field label={t('Wandlerklasse')}><TextInput value={w.wandler_klasse} onChange={v => setzen('wandler_klasse', v)} placeholder={t('z. B. 5P10')}/></Field>
        <Field label={t('Nennstrom Relais [A]')}><TextInput value={w.relais_nennstrom} onChange={v => setzen('relais_nennstrom', v)} placeholder={t('z. B. 1')}/></Field>
      </Row>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.gray500, margin: '6px 0 4px' }}>{t('Einstellwerte')}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 8 }}>
        {EINSTELLWERTE.map(([k, label]) => (
          <Field key={k} label={label}><TextInput value={w[k]} onChange={v => setzen(k, v)}/></Field>
        ))}
      </div>
      <Field label={t('Bemerkung')}><TextInput value={w.notiz} onChange={v => setzen('notiz', v)}/></Field>
    </div>
  );
}

// --- Schutzpruefprotokolle als Datei (nur Buero laedt hoch) ------------------------
const BUCKET = 'schutzpruefungen';
export function SchutzpruefDateien({ stationId, feldId, darfHochladen }) {
  const [liste, setListe] = useState([]);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState(null);
  const [datum, setDatum] = useState('');

  const laden = useCallback(async () => {
    if (!feldId) return;
    try {
      const { data, error } = await withTimeout(supabase.from('station_schutzpruefungen')
        .select('id, pruefdatum, datei_pfad, datei_name, erstellt_am').eq('ms_feld_id', feldId)
        .order('pruefdatum', { ascending: false, nullsFirst: false }), 20000, 'Prüfprotokolle laden');
      if (error) throw error;
      setListe(data || []);
      setFehler(null);
    } catch (e) {
      // Vor Migration 58 gibt es die Tabelle noch nicht - dann einfach nichts zeigen.
      setFehler(/does not exist|schema cache/i.test(e.message || '') ? null : (e.message || String(e)));
    }
  }, [feldId]);
  useEffect(() => { laden(); }, [laden]);

  if (!feldId) {
    return <div style={{ fontSize: 13, color: C.gray500, marginTop: 6 }}>{t('Prüfprotokoll: erst die Station speichern, dann hochladen.')}</div>;
  }

  const hochladen = async (datei) => {
    if (!datei) return;
    setLaedt(true); setFehler(null);
    try {
      const sauber = datei.name.replace(/[^\w.-]+/g, '_');
      const pfad = stationId + '/' + feldId + '/' + Date.now() + '-' + sauber;
      const up = await withTimeout(supabase.storage.from(BUCKET).upload(pfad, datei, { upsert: false, contentType: datei.type || undefined }), 120000, 'Hochladen');
      if (up.error) throw up.error;
      const ins = await withTimeout(supabase.from('station_schutzpruefungen').insert({
        station_id: stationId, ms_feld_id: feldId, pruefdatum: datum || null, datei_pfad: pfad, datei_name: datei.name,
      }), 20000, 'Speichern');
      if (ins.error) throw ins.error;
      setDatum('');
      await laden();
    } catch (e) {
      setFehler(t('Hochladen fehlgeschlagen: {fehler}', { fehler: e.message || String(e) }));
    } finally {
      setLaedt(false);
    }
  };
  const oeffnen = async (eintrag) => {
    // Fenster sofort oeffnen (Popup-Sperre), Adresse danach setzen.
    const fenster = window.open('', '_blank');
    try {
      const { data, error } = await withTimeout(supabase.storage.from(BUCKET).createSignedUrl(eintrag.datei_pfad, 300), 20000, 'Link');
      if (error) throw error;
      if (fenster) fenster.location.href = data.signedUrl; else window.location.href = data.signedUrl;
    } catch (e) {
      if (fenster) fenster.close();
      setFehler(t('Öffnen fehlgeschlagen: {fehler}', { fehler: e.message || String(e) }));
    }
  };
  const loeschen = async (eintrag) => {
    if (!window.confirm(t('Prüfprotokoll „{name}“ wirklich löschen?', { name: eintrag.datei_name || '' }))) return;
    try {
      const del = await withTimeout(supabase.from('station_schutzpruefungen').delete().eq('id', eintrag.id), 20000, 'Löschen');
      if (del.error) throw del.error;
      await supabase.storage.from(BUCKET).remove([eintrag.datei_pfad]).catch(() => {});
      await laden();
    } catch (e) {
      setFehler(t('Löschen fehlgeschlagen: {fehler}', { fehler: e.message || String(e) }));
    }
  };
  const datumText = (d) => (d ? String(d).slice(8, 10) + '.' + String(d).slice(5, 7) + '.' + String(d).slice(0, 4) : '–');

  return (
    <div style={{ marginTop: 8, padding: 10, background: C.gray100, borderRadius: 7 }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: C.navy, marginBottom: 6 }}>{t('Schutzprüfprotokolle')}</div>
      {liste.length === 0 && <div style={{ fontSize: 13, color: C.gray500 }}>{t('Noch kein Prüfprotokoll hinterlegt.')}</div>}
      {liste.map(e => (
        <div key={e.id} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontSize: 14, padding: '4px 0' }}>
          <span style={{ color: C.gray700, minWidth: 90 }}>{datumText(e.pruefdatum)}</span>
          <button type="button" onClick={() => oeffnen(e)} style={{ background: 'none', border: 'none', padding: 0, color: C.blue, fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>{e.datei_name || t('Datei öffnen')}</button>
          {darfHochladen && <button type="button" onClick={() => loeschen(e)} style={{ background: 'none', border: 'none', padding: 0, color: C.red, cursor: 'pointer', fontSize: 13 }}>{t('löschen')}</button>}
        </div>
      ))}
      {darfHochladen && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
          <label style={{ fontSize: 13, color: C.gray700 }}>{t('Prüfdatum')}{' '}
            <input type="date" value={datum} onChange={e => setDatum(e.target.value)} style={{ padding: '6px 8px', border: '1px solid ' + C.gray200, borderRadius: 6 }}/>
          </label>
          <label style={{ padding: '7px 12px', borderRadius: 6, background: laedt ? C.gray300 : C.navy, color: C.white, fontWeight: 600, fontSize: 13, cursor: laedt ? 'wait' : 'pointer' }}>
            {laedt ? t('wird hochgeladen …') : t('+ Prüfprotokoll hochladen (PDF/Foto)')}
            <input type="file" accept="application/pdf,image/*" disabled={laedt} style={{ display: 'none' }}
              onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; hochladen(f); }}/>
          </label>
        </div>
      )}
      {fehler && <div style={{ marginTop: 6, fontSize: 13, color: C.red, fontWeight: 600 }}>{fehler}</div>}
    </div>
  );
}
